import type { AxiosError, AxiosInstance } from 'axios'

import { resolvePayAccessTokenForMicroservices } from '../services/pay-auth-bridge.service'
import { clearPayAccessTokenCache } from '../services/pay-auth-bridge.service'
import { clearSuiteAccessTokenCache } from '../services/suite-access-token.service'
import { isSuiteSsoRedirectMode } from '../sso-flags'
import { logInfo, logWarn } from '../utils/sso-logger'

function isExpiredAuthError(error: AxiosError<{ message?: string; errors?: string[] }>): boolean {
  if (error.response?.status !== 401) {
    return false
  }

  const message = String(error.response?.data?.message ?? '').toLowerCase()
  const errors = error.response?.data?.errors ?? []

  return message.includes('expired') || errors.includes('ACCESS_TOKEN_EXPIRED')
}

/**
 * Pay microservices return 401 when the bridged Pay access token expired.
 * Re-bridge via Identity → /auth/request-access-token and retry once.
 */
export function attachMicroserviceAuthRetry(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ message?: string; errors?: string[] }>) => {
      const originalRequest = error.config as typeof error.config & { _suiteAuthRetry?: boolean }
      const requestUrl = `${originalRequest?.baseURL ?? ''}${originalRequest?.url ?? ''}`

      if (!isSuiteSsoRedirectMode() || !originalRequest || originalRequest._suiteAuthRetry) {
        return Promise.reject(error)
      }

      if (!isExpiredAuthError(error)) {
        return Promise.reject(error)
      }

      originalRequest._suiteAuthRetry = true
      logWarn('microservice-auth-retry — expired Pay access token, re-bridging auth', { requestUrl })

      clearSuiteAccessTokenCache()
      clearPayAccessTokenCache()

      try {
        const token = await resolvePayAccessTokenForMicroservices()
        originalRequest.headers.Authorization = `Bearer ${token}`

        logInfo('microservice-auth-retry — retrying request with fresh Pay access token', { requestUrl })
        return client(originalRequest)
      } catch (retryError) {
        return Promise.reject(retryError)
      }
    }
  )
}
